import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { buildEdgeFunctionHeaders } from "@/lib/session";

export interface ColumnInfo {
  column_name: string;
  data_type: string;
  is_nullable?: string;
}

export interface TableSchema {
  table_name: string;
  columns: ColumnInfo[];
}

async function fetchSchema(): Promise<TableSchema[]> {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  const headers = await buildEdgeFunctionHeaders();

  const res = await fetch(`${supabaseUrl}/functions/v1/mcp-api/getSchema`, {
    method: "GET",
    headers,
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: "Failed to fetch schema" }));
    throw new Error(err.error || `Request failed with status ${res.status}`);
  }

  const data = await res.json();

  let tableList: TableSchema[] = [];
  if (Array.isArray(data)) {
    tableList = data;
  } else if (data && typeof data === "object") {
    if (data.tables && !Array.isArray(data.tables) && typeof data.tables === "object") {
      tableList = Object.entries(data.tables).map(([name, cols]: [string, any]) => ({
        table_name: name,
        columns: (Array.isArray(cols) ? cols : []).map((c: any) => ({
          column_name: c.Field || c.column_name || c.name || "",
          data_type: c.Type || c.data_type || c.type || "",
          is_nullable: c.Null ?? c.is_nullable ?? c.nullable ?? undefined,
        })),
      }));
    } else if (Array.isArray(data.tables)) {
      tableList = data.tables;
    } else if (Array.isArray(data.tableList)) {
      tableList = data.tableList;
    } else if (Array.isArray(data.list)) {
      tableList = data.list;
    }
    // Normalize
    if (tableList.length > 0 && !(tableList[0] as any).columns) {
      tableList = tableList.map((t: any) => ({
        table_name: t.table_name || t.name || "",
        columns: (t.columns || t.fields || []).map((c: any) => ({
          column_name: c.Field || c.column_name || c.name || "",
          data_type: c.Type || c.data_type || c.type || "",
          is_nullable: c.Null ?? c.is_nullable ?? c.nullable ?? undefined,
        })),
      }));
    }
  }

  tableList.sort((a, b) => a.table_name.localeCompare(b.table_name));
  return tableList;
}

export function useMcpSchema() {
  const query = useQuery({
    queryKey: ["mcp-schema"],
    queryFn: fetchSchema,
    staleTime: 5 * 60 * 1000, // cache for 5 min
  });

  const tables = (query.data || []).map((t) => t.table_name);
  const columns: Record<string, string[]> = {};
  for (const t of query.data || []) {
    columns[t.table_name] = t.columns.map((c) => c.column_name);
  }

  return {
    schemaData: query.data || [],
    tables,
    columns,
    isLoading: query.isLoading || query.isFetching,
    error: query.error,
    refetch: query.refetch,
  };
}
